/**
 * incident.io Widget API — status mapping utilities
 *
 * Endpoint format:
 *   GET https://statuspage.incident.io/{org-slug}/api/widget
 *
 * The org-slug is the subdomain/path segment of your published status page.
 * To find it: incident.io dashboard → Status pages → your page → Settings → Widget API.
 * The full URL is shown there and can be previewed with a live JSON sample.
 *
 * The endpoint is unauthenticated and publicly accessible (public status pages only).
 * It is strongly recommended to cache responses for 30–60 s on a backend proxy;
 * for a purely-frontend integration (this module), poll every 120 s instead.
 *
 * Response shape (abbreviated):
 * {
 *   ongoing_incidents: Array<{
 *     id: string;
 *     name: string;
 *     status: "investigating" | "identified" | "monitoring" | "resolved";
 *     affected_components: Array<{ id: string; name: string; status: string }>;
 *   }>;
 *   in_progress_maintenances: Array<{
 *     id: string;
 *     name: string;
 *     status: "maintenance_in_progress";
 *   }>;
 *   scheduled_maintenances: Array<{
 *     id: string;
 *     name: string;
 *     status: "maintenance_scheduled";
 *   }>;
 * }
 */

import { z } from 'zod';

export type SystemStatusLevel =
  | 'operational'
  | 'degraded'
  | 'outage'
  | 'maintenance'
  | 'unknown';

export interface SystemStatusResult {
  level: SystemStatusLevel;
  ongoingCount: number;
  maintenanceCount: number;
}

/**
 * Incident statuses that represent a full service outage vs a degradation.
 * "investigating" and "identified" are treated as full outage (we don't yet
 * know the scope); "monitoring" (fix applied, watching) is treated as degraded.
 */
const OUTAGE_STATUSES = new Set(['investigating', 'identified']);
const DEGRADED_STATUSES = new Set(['monitoring']);

const IncidentSchema = z.looseObject({ status: z.string() });

const WidgetResponseSchema = z.looseObject({
  ongoing_incidents: z.array(IncidentSchema),
  in_progress_maintenances: z.array(z.looseObject({})),
});

const UNKNOWN_RESULT: SystemStatusResult = {
  level: 'unknown',
  ongoingCount: 0,
  maintenanceCount: 0,
};

/**
 * Maps a raw incident.io widget API response to a simplified SystemStatusResult.
 *
 * Rules (highest-severity wins):
 *   1. Any incident with investigating/identified → outage
 *   2. Any incident with monitoring → degraded
 *   3. Any in-progress maintenance → maintenance
 *   4. Nothing active → operational
 *   5. Response not parseable → unknown
 */
export function mapWidgetResponseToStatus(raw: unknown): SystemStatusResult {
  const parsed = WidgetResponseSchema.safeParse(raw);
  if (!parsed.success) return UNKNOWN_RESULT;

  const { ongoing_incidents: ongoing, in_progress_maintenances: maintenance } =
    parsed.data;
  const ongoingCount = ongoing.length;
  const maintenanceCount = maintenance.length;

  const hasOutage = ongoing.some((incident) =>
    OUTAGE_STATUSES.has(incident.status)
  );
  const hasDegraded = ongoing.some((incident) =>
    DEGRADED_STATUSES.has(incident.status)
  );

  if (hasOutage) {
    return { level: 'outage', ongoingCount, maintenanceCount };
  }
  if (hasDegraded) {
    return { level: 'degraded', ongoingCount, maintenanceCount };
  }
  if (ongoingCount > 0) {
    // Unknown incident status — treat as degraded to be safe.
    return { level: 'degraded', ongoingCount, maintenanceCount };
  }
  if (maintenanceCount > 0) {
    return { level: 'maintenance', ongoingCount, maintenanceCount };
  }

  return { level: 'operational', ongoingCount: 0, maintenanceCount: 0 };
}
